const API_BASE = "/api";

/** One daily quest as served to the client (server computes progress/complete). */
export interface QuestView {
  id: string;
  type: string;
  label: string;
  target: number;
  reward: number;
  progress: number;
  complete: boolean;
  claimed: boolean;
}

interface QuestsPayload {
  day: string;
  quests: QuestView[];
}

/**
 * Thin API client + cache for the daily-quests system, mirroring the shape of
 * `Friends`/`Races`. The server is the source of truth for progress and reward
 * eligibility; this just caches the latest snapshot so scenes can render
 * frame-driven, and applies mutation responses straight back into the cache. A
 * monotonic ticket guard keeps a slow background poll from overwriting the
 * fresher state a claim just returned.
 */
export class Quests {
  private quests: QuestView[] = [];
  private seq = 0;
  private applied = 0;

  list(): QuestView[] {
    return this.quests.slice();
  }

  /** Completed-but-unclaimed quests — drives the menu badge. */
  claimableCount(): number {
    return this.quests.filter((q) => q.complete && !q.claimed).length;
  }

  clear(): void {
    this.quests = [];
  }

  private apply(ticket: number, data: Partial<QuestsPayload>): void {
    if (ticket < this.applied || !Array.isArray(data.quests)) return;
    this.applied = ticket;
    this.quests = data.quests;
  }

  /** Pull today's quests + progress. Safe to call on a timer. */
  async refresh(name: string, token: string): Promise<void> {
    if (!name || !token) return;
    const ticket = ++this.seq;
    try {
      const res = await fetch(`${API_BASE}/quests?name=${encodeURIComponent(name)}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      this.apply(ticket, (await res.json()) as Partial<QuestsPayload>);
    } catch {
      // keep cache on network error
    }
  }

  /** Claim a completed quest's reward. Resolves the flies awarded + new wallet, or null on failure. */
  async claim(
    name: string,
    token: string,
    questId: string,
  ): Promise<{ reward: number; wallet: number } | null> {
    const ticket = ++this.seq;
    try {
      const res = await fetch(`${API_BASE}/quests/claim`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, questId }),
      });
      const data = (await res.json().catch(() => ({}))) as Partial<QuestsPayload> & {
        ok?: boolean;
        reward?: number;
        wallet?: number;
      };
      if (!res.ok || !data.ok) return null;
      this.apply(ticket, data);
      return {
        reward: Math.max(0, Number(data.reward) || 0),
        wallet: Math.max(0, Number(data.wallet) || 0),
      };
    } catch {
      return null;
    }
  }
}
